// Ported from ipawsAudio.utils.js on 2026-10-06.
import { IPAWS_AUDIO_LIMITS } from '../components/IpawsAlertFields/config/Ipaws.config';

export const IPAWS_EAS_AUDIO_MAXIMUM_BYTES = IPAWS_AUDIO_LIMITS['maximum-combined-bytes'];
export const IPAWS_EAS_AUDIO_SIZE_ERROR =
  'The combined audio files must be 4 MiB (4,194,304 bytes) or smaller.';
// IDG v4.06 Table 81 p79 allows at most two minutes of EAS broadcast content.
export const IPAWS_EAS_AUDIO_MAXIMUM_DURATION_SECONDS =
  IPAWS_AUDIO_LIMITS['maximum-duration-seconds'];
export const IPAWS_EAS_AUDIO_DURATION_ERROR =
  'The MP3 file must be 2 minutes or shorter. EAS broadcast content is two minutes at most (IDG v4.06 Table 81 p79).';
export const IPAWS_EAS_AUDIO_RESOURCE_DESCRIPTION = 'EAS Broadcast Content';
export const IPAWS_EAS_AUDIO_RESOURCE_MIME_TYPE = 'audio/x-ipaws-audio-mp3';
export const IPAWS_EAS_SPANISH_AUDIO_BLOCK_ERROR =
  'Spanish audio is attached, but the alert has no Spanish (es-US) language block. Add the Spanish text or remove the Spanish audio.';

/**
 * Checks that attached Spanish audio has a Spanish language block to go into.
 * The backend puts the `es-US` part in the block whose language is `es-US` (case is ignored) and
 * refuses the file when that block is missing; this reports it before the request.
 * @param info - The info blocks of the submission payload.
 * @param spanishAudioFile - The Spanish audio file, when one is attached.
 * @returns The error, or an empty string when the attachment has its block.
 */
export const getIpawsSpanishAudioBlockError = (
  info: readonly { language?: string }[] | undefined,
  spanishAudioFile: File | null | undefined,
): string =>
  spanishAudioFile &&
  !(info ?? []).some((block) => block.language?.trim().toLowerCase() === 'es-us')
    ? IPAWS_EAS_SPANISH_AUDIO_BLOCK_ERROR
    : '';

/**
 * Checks the combined raw size of the attached audio files.
 * @param files - Every audio file included in the submission.
 * @returns The size error, or an empty string when the total is allowed.
 */
export const getIpawsCombinedAudioFileError = (
  files: readonly (File | null | undefined)[],
): string =>
  files.reduce((total, file) => total + (file?.size ?? 0), 0) > IPAWS_EAS_AUDIO_MAXIMUM_BYTES
    ? IPAWS_EAS_AUDIO_SIZE_ERROR
    : '';

/**
 * Checks an MP3 file against the remaining combined audio allowance.
 * @param file - The candidate file selected by the operator.
 * @param otherAudioBytes - The size of other audio attachments in the submission.
 * @returns The file error, or an empty string when the candidate is allowed.
 */
export const getIpawsAudioFileError = (file: unknown, otherAudioBytes = 0): string => {
  if (!(file instanceof File)) return 'Select an MP3 audio file.';
  if (!file.name.toLowerCase().endsWith('.mp3')) return 'Only .mp3 audio files are supported.';
  if (!['audio/mpeg', 'audio/mp3'].includes(file.type)) {
    return 'The selected file must use an MP3 audio type.';
  }
  if (file.size + otherAudioBytes > IPAWS_EAS_AUDIO_MAXIMUM_BYTES) {
    return IPAWS_EAS_AUDIO_SIZE_ERROR;
  }
  return '';
};

/**
 * Reads the playing time of an audio file.
 * @param file - The audio file to read.
 * @returns The duration in seconds.
 * @throws {Error} The browser cannot read the file or its duration.
 */
export const getIpawsAudioDuration = (file: File): Promise<number> =>
  new Promise((resolve, reject) => {
    const objectUrl = URL.createObjectURL(file);
    const audio = new Audio();
    const cleanUp = () => {
      audio.removeAttribute('src');
      audio.load();
      URL.revokeObjectURL(objectUrl);
    };

    audio.onloadedmetadata = () => {
      const { duration } = audio;
      cleanUp();
      if (!Number.isFinite(duration)) reject(new Error('Audio duration is unavailable.'));
      else resolve(duration);
    };
    audio.onerror = () => {
      cleanUp();
      reject(new Error('The MP3 file could not be read.'));
    };
    audio.preload = 'metadata';
    audio.src = objectUrl;
  });

/**
 * Formats audio playing time as minutes and seconds.
 * @param durationSeconds - The playing time in seconds.
 * @returns The formatted playing time.
 */
export const formatIpawsAudioDuration = (durationSeconds: number): string => {
  const totalSeconds = Math.max(0, Math.round(Number(durationSeconds) || 0));
  const minutes = Math.floor(totalSeconds / 60);
  return `${minutes}:${String(totalSeconds % 60).padStart(2, '0')}`;
};
